import {
  createProduct,
  createSKU,
  getProductCatalog,
  getProductTypes,
  getSKUs,
  setProductTypes,
  syncProductOptions,
  updateSKU,
} from '../../../app/api';
import { getLanguage } from '../../../app/i18n';
import { getSavedSimulation, resolveCostFromSimulation } from '../../../extensions/price-simulator-3d/priceSimulatorMetadata';
import {
  catalogAttributesForSku,
  catalogAttributesRequiredForServerSku,
  nameToAttributeKey,
} from './lineItemCatalogAttributeUtils';
import { detectNewDraftAttributes } from '../../core/public/productOptionSyncUtils';

export const LINE_ITEM_PRODUCT_NAME_MAX = 120;

export const SKU_LINK_CREATE_NEW = 'create_new';
export const SKU_LINK_EXISTING = 'link_existing';
export const SKU_LINK_REUSE_MATCH = 'reuse_match';

async function assignFirstProductType(productId) {
  const res = await getProductTypes({ limit: 100 });
  const list = (res.data || [])
    .map((row) => ({
      id: row.id,
      display_order: row.display_order ?? row.displayOrder ?? 0,
    }))
    .filter((t) => t.id);
  list.sort((a, b) => a.display_order - b.display_order);
  if (!list.length) return;
  await setProductTypes(productId, { type_ids: [list[0].id] });
}

export function truncateProductName(description) {
  const text = String(description ?? '').trim();
  if (!text) return '';
  if (text.length <= LINE_ITEM_PRODUCT_NAME_MAX) return text;
  return text.slice(0, LINE_ITEM_PRODUCT_NAME_MAX);
}

export function attributeOptionStrings(attr) {
  const opts = attr.options || [];
  const out = [];
  opts.forEach((opt) => {
    const v = typeof opt === 'string' ? opt : String(opt?.value ?? opt?.label ?? '');
    const trimmed = String(v ?? '').trim();
    if (trimmed) out.push(trimmed);
  });
  return out;
}

export function attributeOptionsForDisplay(attr, extraOptionsByAttrKey = {}) {
  const key = attr?.key || attr?.attribute_key;
  const base = attributeOptionStrings(attr);
  const extra = key ? extraOptionsByAttrKey[key] || [] : [];
  const seen = new Set(base);
  const out = [...base];
  extra.forEach((raw) => {
    const trimmed = String(raw ?? '').trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      out.push(trimmed);
    }
  });
  return out;
}

export function canonicalCombinationKeyFromMap(attrsMap) {
  const keys = Object.keys(attrsMap || {}).sort();
  return keys.map((k) => `${k}=${String(attrsMap[k] ?? '').trim()}`).join('|');
}

export function skuAttributesToMap(attributes) {
  const map = {};
  if (!attributes) return map;
  if (typeof attributes === 'object' && !Array.isArray(attributes)) {
    Object.entries(attributes).forEach(([key, value]) => {
      const k = String(key ?? '').trim();
      const v = String(value ?? '').trim();
      if (k && v) map[k] = v;
    });
    return map;
  }
  if (Array.isArray(attributes)) {
    attributes.forEach((a) => {
      const k = String(a.key || a.attribute_key || '').trim();
      const v = String(a.value ?? a.attribute_value ?? '').trim();
      if (k && v) map[k] = v;
    });
  }
  return map;
}

export function findSkuByAttributeMap(skus, attrMap) {
  const targetKey = canonicalCombinationKeyFromMap(attrMap);
  if (!targetKey) return null;
  for (const sku of skus || []) {
    if ((sku.status || 'ACTIVE') !== 'ACTIVE') continue;
    const skuMap = skuAttributesToMap(sku.attributes);
    if (canonicalCombinationKeyFromMap(skuMap) === targetKey) return sku;
  }
  return null;
}

export function buildSkuMetadataFromFormItem(formMetadata) {
  if (!formMetadata || typeof formMetadata !== 'object') return undefined;
  const keys = Object.keys(formMetadata);
  if (keys.length === 0) return undefined;
  const out = {};
  keys.forEach((key) => {
    if (formMetadata[key] != null) out[key] = formMetadata[key];
  });
  return Object.keys(out).length > 0 ? out : undefined;
}

export function catalogOptionsToAttributes(catalog) {
  const options = catalog?.options || [];
  return options.map((o) => ({
    id: o.id,
    key: o.key,
    attribute_key: o.key,
    name: o.name,
    is_variation: true,
    is_required: true,
    is_customizable: false,
    display_order: o.display_order ?? 0,
    options: (o.values || []).map((v) => ({ value: v })),
  }));
}

export async function loadProductCatalogAttributes(productId) {
  const res = await getProductCatalog(productId);
  const catalog = res.data ?? {};
  return catalogOptionsToAttributes(catalog);
}

export async function persistDraftAttributes(productId, draftAttributes, defaultValuesForExisting = {}) {
  const options = (draftAttributes || [])
    .filter((d) => String(d.name ?? '').trim())
    .map((d) => ({
      name: String(d.name).trim(),
      key: nameToAttributeKey(d.name),
      values: (d.options || []).map((o) => String(o).trim()).filter(Boolean),
    }))
    .filter((o) => o.values.length > 0);
  if (options.length === 0) return;
  const payload = { options };
  const defaults = defaultValuesForExisting || {};
  if (Object.keys(defaults).length > 0) {
    payload.default_values_for_existing = defaults;
  }
  await syncProductOptions(productId, payload);
}

export async function persistPendingNewOptions(
  productId,
  catalogAttributes,
  pendingNewOptions,
  attributeValues,
  extraOptionsByAttrKey = {},
) {
  const values = { ...(attributeValues || {}) };
  const variationAttrs = (catalogAttributes || []).filter((a) => a.is_variation && !a._isDraft);
  const optionsPayload = variationAttrs.map((attr) => {
    const attrKey = attr.key || attr.attribute_key;
    const valueSet = new Set(attributeOptionStrings(attr));
    const selected = String(values[attrKey] ?? '').trim();
    if (selected) valueSet.add(selected);
    const pending = String((pendingNewOptions || {})[attrKey] ?? '').trim();
    if (pending) valueSet.add(pending);
    (extraOptionsByAttrKey[attrKey] || []).forEach((o) => {
      const t = String(o ?? '').trim();
      if (t) valueSet.add(t);
    });
    if (selected && attrKey) values[attrKey] = selected;
    else if (pending && attrKey) values[attrKey] = pending;
    return {
      name: attr.name,
      key: attrKey,
      values: [...valueSet],
    };
  }).filter((o) => o.key && o.values.length > 0);

  if (optionsPayload.length > 0) {
    await syncProductOptions(productId, { options: optionsPayload });
  }
  return values;
}

export function buildDefaultValuesForNewDrafts(catalogAttributes, draftAttributes, attributeValues) {
  const newDrafts = detectNewDraftAttributes(catalogAttributes, draftAttributes);
  const defaults = {};
  newDrafts.forEach((opt) => {
    const chosen =
      String(attributeValues?.[opt.key] ?? '').trim() ||
      String(opt.values?.[0] ?? '').trim();
    if (chosen) defaults[opt.key] = chosen;
  });
  return defaults;
}

export async function applySkuPriceAndMetadata(skuId, skuRow, price, formMetadata, { updatePrice = true } = {}) {
  const metadata = buildSkuMetadataFromFormItem(formMetadata);
  const savedSimulation = getSavedSimulation(formMetadata);
  const currentCost = Number(skuRow?.cost ?? skuRow?.metadata?.cost ?? 0);
  const cost = resolveCostFromSimulation(savedSimulation, currentCost);
  const attrs = skuAttributesToMap(skuRow?.attributes);
  const payload = {
    code: skuRow?.code || skuId,
    price: updatePrice ? Math.max(0, Number(price) || 0) : (skuRow?.price ?? 0),
    price_type: skuRow?.price_type || 'FIXED',
    stock: skuRow?.is_made_to_order ? 0 : (Number(skuRow?.stock) || 0),
    stock_unit: (skuRow?.stock_unit ?? '').toString(),
    is_made_to_order: !!skuRow?.is_made_to_order,
    manufacturing_days: skuRow?.is_made_to_order ? (parseInt(skuRow?.manufacturing_days, 10) || 0) : 0,
    status: skuRow?.status || 'ACTIVE',
    attributes: attrs,
  };
  const mergedMetadata = {
    ...(skuRow?.metadata || {}),
    ...(metadata || {}),
    cost,
  };
  if (Object.keys(mergedMetadata).length > 0) {
    payload.metadata = mergedMetadata;
  }
  await updateSKU(skuId, payload);
}

export function validateAttributeValues(
  catalogAttributes,
  attributeValues,
  extraOptionsByAttrKey = {},
) {
  const required = catalogAttributesForSku(catalogAttributes);
  for (const attr of required) {
    const key = attr.key || attr.attribute_key;
    if (!key) continue;
    const val = String(attributeValues[key] ?? '').trim();
    if (!val) {
      throw new Error(`variation attribute required: ${attr.name || key}`);
    }
    const opts = attributeOptionsForDisplay(attr, extraOptionsByAttrKey);
    if (opts.length > 0 && !opts.includes(val)) {
      throw new Error(`invalid value for ${attr.name || key}`);
    }
  }
}

export function buildAttributeMap(catalogAttributes, attributeValues) {
  const map = {};
  catalogAttributesForSku(catalogAttributes).forEach((attr) => {
    const key = attr.key || attr.attribute_key;
    if (!key) return;
    const val = String(attributeValues[key] ?? '').trim();
    if (val) map[key] = val;
  });
  return map;
}

export function buildSkuAttributeMap(
  catalogAttributes,
  attributeValues,
  extraOptionsByAttrKey = {},
) {
  const map = {};
  catalogAttributesRequiredForServerSku(catalogAttributes).forEach((attr) => {
    const key = attr.key || attr.attribute_key;
    if (!key) return;
    const fromForm = String(attributeValues[key] ?? '').trim();
    if (fromForm) {
      map[key] = fromForm;
      return;
    }
    const opts = attributeOptionsForDisplay(attr, extraOptionsByAttrKey);
    if (opts.length === 1) {
      map[key] = opts[0];
    }
  });
  return map;
}

export function validateSkuAttributesForCreate(
  catalogAttributes,
  attributeValues,
  extraOptionsByAttrKey = {},
) {
  const required = catalogAttributesRequiredForServerSku(catalogAttributes);
  const attrMap = buildSkuAttributeMap(catalogAttributes, attributeValues, extraOptionsByAttrKey);
  const missing = required
    .filter((attr) => {
      const key = attr.key || attr.attribute_key;
      return key && !String(attrMap[key] ?? '').trim();
    })
    .map((attr) => attr.name || attr.key || '');
  if (missing.length > 0) {
    throw new Error(`Preencha todos os atributos do produto: ${missing.filter(Boolean).join(', ')}`);
  }
  for (const attr of required) {
    const key = attr.key || attr.attribute_key;
    const val = String(attrMap[key] ?? '').trim();
    const opts = attributeOptionsForDisplay(attr, extraOptionsByAttrKey);
    if (opts.length > 0 && !opts.includes(val)) {
      throw new Error(`Valor inválido para o atributo "${attr.name || key}"`);
    }
  }
  return attrMap;
}

async function resolveSkuForProduct({
  productId,
  productCode,
  price,
  formMetadata,
  skuLinkStrategy,
  existingSkuId,
  attributeValues,
  draftAttributes,
  pendingNewOptions,
  extraOptionsByAttrKey,
  defaultValuesForExisting,
}) {
  const pid = String(productId ?? '').trim();
  if (!pid) throw new Error('Product is required');

  if (skuLinkStrategy === SKU_LINK_EXISTING) {
    const skuId = String(existingSkuId ?? '').trim();
    if (!skuId) throw new Error('SKU is required');
    const skusRes = await getSKUs(pid);
    const sku = (skusRes.data || []).find((s) => s.id === skuId);
    if (!sku) throw new Error('SKU not found');
    await applySkuPriceAndMetadata(skuId, sku, price, formMetadata);
    return { skuId, productId: pid, productCode: productCode || pid, reused: true, created: false };
  }

  if (draftAttributes?.length) {
    let catalogBefore = await loadProductCatalogAttributes(pid);
    const newDrafts = detectNewDraftAttributes(catalogBefore, draftAttributes);
    let defaults = defaultValuesForExisting || {};
    if (newDrafts.length > 0 && Object.keys(defaults).length === 0) {
      const skusRes = await getSKUs(pid);
      if ((skusRes.data || []).length > 0) {
        defaults = buildDefaultValuesForNewDrafts(catalogBefore, draftAttributes, attributeValues);
      }
    }
    await persistDraftAttributes(pid, draftAttributes, defaults);
  }

  let catalogAttributes = await loadProductCatalogAttributes(pid);
  const mergedValues = await persistPendingNewOptions(
    pid,
    catalogAttributes,
    pendingNewOptions,
    attributeValues,
    extraOptionsByAttrKey,
  );
  catalogAttributes = await loadProductCatalogAttributes(pid);

  const serverRequired = catalogAttributesRequiredForServerSku(catalogAttributes);
  const attrMap =
    serverRequired.length > 0
      ? validateSkuAttributesForCreate(catalogAttributes, mergedValues, extraOptionsByAttrKey)
      : buildAttributeMap(catalogAttributes, mergedValues);

  if (skuLinkStrategy !== SKU_LINK_EXISTING && Object.keys(attrMap).length === 0) {
    throw new Error('variation attributes are required');
  }

  const skusRes = await getSKUs(pid);
  const skus = skusRes.data || [];

  let sku = null;
  let created = false;

  if (Object.keys(attrMap).length > 0) {
    sku = findSkuByAttributeMap(skus, attrMap);
  }

  if (!sku) {
    const createRes = await createSKU(pid, {
      product_id: pid,
      price: Math.max(0, Number(price) || 0),
      price_type: 'FIXED',
      stock: 0,
      stock_unit: '',
      is_made_to_order: false,
      manufacturing_days: 0,
      status: 'ACTIVE',
      attributes: attrMap,
    });
    const createdSku = createRes.data;
    if (!createdSku?.id) throw new Error('SKU creation failed');
    sku = { ...createdSku, attributes: createdSku.attributes ?? attrMap };
    created = true;
  } else {
    await applySkuPriceAndMetadata(sku.id, sku, price, formMetadata);
    return {
      skuId: sku.id,
      productId: pid,
      productCode: productCode || pid,
      reused: true,
      created: false,
    };
  }

  await applySkuPriceAndMetadata(sku.id, sku, price, formMetadata);

  return {
    skuId: sku.id,
    productId: pid,
    productCode: productCode || pid,
    reused: false,
    created: true,
  };
}

export async function registerLineItemAsNewProduct({
  name,
  description,
  type,
  price,
  formMetadata,
  draftAttributes,
  attributeValues,
  pendingNewOptions,
  extraOptionsByAttrKey,
  skuLinkStrategy,
  defaultValuesForExisting,
}) {
  const productRes = await createProduct({
    name: String(name ?? '').trim(),
    description: String(description ?? '').trim(),
    status: 'ACTIVE',
    type: type || 'PRODUCT',
    locale: getLanguage(),
  });
  const product = productRes.data;
  const productId = product?.id;
  if (!productId) throw new Error('Product creation failed');

  await assignFirstProductType(productId);

  const drafts = (draftAttributes || []).filter((d) => String(d.name ?? '').trim());

  if (drafts.length === 0) {
    throw new Error('variation attributes are required');
  }

  return resolveSkuForProduct({
    productId,
    productCode: product.code || productId,
    price,
    formMetadata,
    skuLinkStrategy: skuLinkStrategy || SKU_LINK_CREATE_NEW,
    existingSkuId: null,
    attributeValues,
    draftAttributes,
    pendingNewOptions,
    extraOptionsByAttrKey,
    defaultValuesForExisting,
  });
}

export async function registerLineItemOnExistingProduct(params) {
  return resolveSkuForProduct(params);
}

export function hasSimulatorMetadata(formMetadata) {
  return !!getSavedSimulation(formMetadata);
}
